import type { ReactNode } from 'react'

export type SectionBackground =
  | 'beige'
  | 'light'
  | 'surface'
  | 'dark'
  | 'bordeaux'
export type SectionSpace = 'sm' | 'md' | 'lg'

export interface SectionProps {
  children: ReactNode
  /**
   * Background band. 'beige' (default) is the page ground, 'surface' is warm
   * white, 'dark' is the charcoal contrast band, 'bordeaux' is the accent
   * band (used for the homepage quote CTA), 'light' is a readable alias of
   * 'beige'. Maps to .section--* in index.css.
   */
  background?: SectionBackground
  /**
   * Vertical padding rhythm, via the --space-* scale: 'sm' (48px), 'md'
   * (64px, default), 'lg' (96px). Vary it between sections to build rhythm
   * (brief s.30) instead of giving every band the same height.
   */
  space?: SectionSpace
  /** Element to render. Defaults to <section>. */
  as?: 'section' | 'div' | 'header' | 'footer' | 'aside'
  id?: string
  /** Extra class on the outer full-bleed element. */
  className?: string
}

/**
 * Full-bleed background band whose content stays within --wrap (1080px).
 *
 * Phase 2 foundation: not used on any page yet. Example:
 *
 *   <Section background="dark" space="lg">
 *     <p className="eyebrow">How it works</p>
 *     <h2 className="display">From Chinese port to your line</h2>
 *     <a className="btn btn--on-dark" href="/en/how-to-order">
 *       Request a quote
 *     </a>
 *   </Section>
 *
 * The outer element breaks out of the 1080px .page-container via negative
 * viewport-width margins (see .section in index.css); .section__inner
 * re-clamps the content and applies the horizontal gutter.
 */
function Section({
  children,
  background = 'beige',
  space = 'md',
  as: Tag = 'section',
  id,
  className,
}: SectionProps) {
  const outerClass = [
    'section',
    `section--${background}`,
    `section--space-${space}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Tag className={outerClass} id={id}>
      <div className="section__inner">{children}</div>
    </Tag>
  )
}

export default Section
